import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaPlus, FaStore, FaEdit, FaTrash, FaEuroSign, FaCalendarAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../core/api/client';
import './Sales.css';

function Sales() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    payment_type: 'cash',
    sale_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchSales();
  }, []);

  const fetchSales = async () => {
    try {
      const response = await api.get('/sales');
      setSales(response.data);
    } catch (error) {
      toast.error('Chyba pri načítaní predajov');
      console.error('Error fetching sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description || !formData.amount) {
      toast.error('Vyplňte všetky povinné polia');
      return;
    }

    try {
      const data = {
        description: formData.description,
        amount: parseFloat(formData.amount),
        payment_type: formData.payment_type,
        sale_date: new Date(formData.sale_date).toISOString()
      };

      if (editingId) {
        await api.put(`/sales/${editingId}`, data);
        toast.success('Predaj aktualizovaný');
      } else {
        await api.post('/sales', data);
        toast.success('Predaj pridaný');
      }

      fetchSales();
      resetForm();
    } catch (error) {
      toast.error('Chyba pri ukladaní predaja');
      console.error('Error saving sale:', error);
    }
  };

  const handleEdit = (sale) => {
    setEditingId(sale.id);
    setFormData({
      description: sale.description,
      amount: sale.amount,
      payment_type: sale.payment_type || 'cash',
      sale_date: sale.sale_date.split('T')[0]
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Naozaj chcete vymazať tento predaj?')) {
      try {
        await api.delete(`/sales/${id}`);
        toast.success('Predaj vymazaný');
        fetchSales();
      } catch (error) {
        toast.error('Chyba pri mazaní predaja');
        console.error('Error deleting sale:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      description: '',
      amount: '',
      payment_type: 'cash',
      sale_date: new Date().toISOString().split('T')[0]
    });
    setEditingId(null);
    setShowForm(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sk-SK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateTotal = () => {
    return sales.reduce((sum, sale) => sum + sale.amount, 0).toFixed(2);
  };

  const calculateTodayTotal = () => {
    const today = new Date().toDateString();
    const todaySales = sales.filter(sale =>
      new Date(sale.sale_date).toDateString() === today
    );
    return todaySales.reduce((sum, sale) => sum + sale.amount, 0).toFixed(2);
  };

  const getFilteredSales = () => {
    if (!selectedMonth) return sales;

    return sales.filter(sale => {
      const saleDate = new Date(sale.sale_date);
      const saleMonth = `${saleDate.getFullYear()}-${String(saleDate.getMonth() + 1).padStart(2, '0')}`;
      return saleMonth === selectedMonth;
    });
  };

  const calculateMonthTotal = () => {
    const filteredSales = getFilteredSales();
    return filteredSales.reduce((sum, sale) => sum + sale.amount, 0).toFixed(2);
  };

  const getAvailableMonths = () => {
    const months = new Set();
    sales.forEach(sale => {
      const date = new Date(sale.sale_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months.add(monthKey);
    });
    return Array.from(months).sort().reverse();
  };

  const formatMonthLabel = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('sk-SK', { month: 'long', year: 'numeric' });
  };

  const filteredSales = getFilteredSales();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div className="sales-page">
      <div className="page-header">
        <h1><FaStore /> Obchod - Predaj</h1>
        <motion.button
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaPlus /> {showForm ? 'Zavrieť' : 'Pridať predaj'}
        </motion.button>
      </div>

      <div className="month-selector-container">
        <label htmlFor="month-select">
          <FaCalendarAlt /> Zobraziť mesiac:
        </label>
        <select
          id="month-select"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="month-selector"
        >
          {getAvailableMonths().map(month => (
            <option key={month} value={month}>
              {formatMonthLabel(month)}
            </option>
          ))}
        </select>
      </div>

      <div className="sales-stats">
        <div className="stat-card highlight">
          <h3>Predaj za {formatMonthLabel(selectedMonth)}</h3>
          <p className="stat-value">€{calculateMonthTotal()}</p>
          <small>{filteredSales.length} predajov</small>
        </div>
        <div className="stat-card">
          <h3>Dnešný predaj</h3>
          <p className="stat-value">€{calculateTodayTotal()}</p>
        </div>
        <div className="stat-card">
          <h3>Celkový predaj (všetko)</h3>
          <p className="stat-value">€{calculateTotal()}</p>
          <small>{sales.length} predajov celkom</small>
        </div>
      </div>

      {showForm && (
        <motion.div
          className="sale-form"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2>{editingId ? 'Upraviť predaj' : 'Nový predaj'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Popis predaja *</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Napr. Svadobné oznámenie - Petra a Ján"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label><FaEuroSign /> Suma *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="form-group">
                <label><FaCalendarAlt /> Dátum predaja</label>
                <input
                  type="date"
                  value={formData.sale_date}
                  onChange={(e) => setFormData({...formData, sale_date: e.target.value})}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Typ platby</label>
              <div className="payment-type-selector">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="payment_type"
                    value="cash"
                    checked={formData.payment_type === 'cash'}
                    onChange={(e) => setFormData({...formData, payment_type: e.target.value})}
                  />
                  <span>💵 Hotovosť</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="payment_type"
                    value="card"
                    checked={formData.payment_type === 'card'}
                    onChange={(e) => setFormData({...formData, payment_type: e.target.value})}
                  />
                  <span>💳 Karta</span>
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-success">
                {editingId ? 'Aktualizovať' : 'Pridať'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Zrušiť
              </button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="sales-table-container">
        <table className="sales-table">
          <thead>
            <tr>
              <th>Dátum</th>
              <th>Popis</th>
              <th>Suma</th>
              <th>Typ</th>
              <th>Akcie</th>
            </tr>
          </thead>
          <tbody>
            {filteredSales.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-state">
                  {sales.length === 0 ? 'Zatiaľ nemáte žiadne predaje' : `Žiadne predaje za ${formatMonthLabel(selectedMonth)}`}
                </td>
              </tr>
            ) : (
              filteredSales.map((sale) => (
                <motion.tr
                  key={sale.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  whileHover={{ backgroundColor: '#f8f9fa' }}
                >
                  <td>{formatDate(sale.sale_date)}</td>
                  <td>{sale.description}</td>
                  <td className="amount">€{sale.amount.toFixed(2)}</td>
                  <td className="payment-type">
                    {sale.payment_type === 'card' ? '💳' : '💵'}
                  </td>
                  <td className="actions">
                    <button
                      className="btn-icon edit"
                      onClick={() => handleEdit(sale)}
                      title="Upraviť"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="btn-icon delete"
                      onClick={() => handleDelete(sale.id)}
                      title="Vymazať"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Sales;